/**
 * Centauri Game Content Data
 *
 * This file contains all game content extracted from the Centauri Script,
 * including questions, narratives, character messages, scoring, and endings.
 */

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface QuestionChoice {
  id: string;
  text: string;
  scores: {
    brain: number;
    heart: number;
    task: number;
    comm: number;
  };
}

export interface Question {
  id: string;
  text: string;
  choices: QuestionChoice[];
  type: 'text' | 'single-choice' | 'multi-choice';
  stage: number;
  notes?: string;
  // Runtime-specific fields
  decisionMethod?: 'individual' | 'majority' | 'consensus' | 'unanimous';
  validationRules?: string[];
  conditionalLogic?: string; // Condition expression like "Q7=B"
}

export interface CharacterMessage {
  character: 'BIOLOGIST' | 'DIVER' | 'FIXER' | 'HISTORIAN' | 'HUNTER' | 'DOC' | 'GEE';
  icon: string;
  message: string;
  condition?: string;
  // Runtime-specific fields
  timing?: 'immediate' | 'delayed' | 'afterQuestion';
  displayTiming?: 'onEntry' | 'afterQuestion' | 'onExit';
}

// New interfaces for enhanced narrative support
export interface NarrativeSegment {
  id: string;
  speaker: 'NARRATOR' | 'MOTHERSHIP' | 'GEE' | 'DOC';
  text: string;
  displayTiming: 'onEntry' | 'afterQuestion' | 'onExit';
}

export interface StageContent {
  stage: number;
  title: string;
  dayOfPermit: string;
  narrative: string[];
  characterMessages: Record<string, CharacterMessage[]>;
  objectives: string[];
  // Runtime-specific fields
  narrativeSequence?: NarrativeSegment[];
  energyFeedback?: EnergyFeedbackMessage[];
}

export interface EnergyChange {
  character: 'HISTORIAN' | 'BIOLOGIST' | 'HUNTER' | 'DIVER' | 'FIXER' | 'DOC' | 'GEE';
  change: number;
  message: string;
  condition?: string;
}

export interface EnergyFeedbackMessage {
  questionId: string;
  choice: string;
  change: string; // e.g., "+30%" or "-20%"
  message: string;
  narrator: 'NARRATOR' | 'DOC' | 'MOTHERSHIP';
  tone: 'positive' | 'negative' | 'neutral';
}

export interface InventoryItem {
  id: string;
  name: string;
  description?: string;
}

export interface Ending {
  id: string;
  title: string;
  condition: string;
  text: string;
  priority: number; // Higher priority endings are checked first
}

// ============================================================================
// QUESTIONS
// ============================================================================

export const QUESTIONS: Question[] = [
  {
    id: 'Q1',
    text: 'What should your teammates call you?',
    choices: [],
    type: 'text',
    stage: 0,
    notes: 'Open text box for player name'
  },
  {
    id: 'Q2',
    text: 'What is your understanding of your team\'s mission thus far?',
    choices: [],
    type: 'text',
    stage: 0,
    notes: 'Open text box for mission understanding'
  },
  {
    id: 'Q3',
    text: 'What would you like your team name to be?',
    choices: [],
    type: 'text',
    stage: 0,
    notes: 'Team name repeated the most will become the name'
  },
  {
    id: 'Q4',
    text: 'As a team which tub do you want to take for the mission?',
    choices: [
      {
        id: 'A',
        text: 'Tub A (Bio transport capsules, Environmental Cleanse, Extra Diving Bell parts, Extra nets and fins, Two energy meals)',
        scores: { brain: 0, heart: 0, task: 1, comm: 1 }
      },
      {
        id: 'B',
        text: 'Tub B (Bio transport capsules, Emergency decomp suit, Environmental Cleanse, Extra nets and fins, Translation device)',
        scores: { brain: 0, heart: 0, task: 1, comm: 1 }
      },
      {
        id: 'C',
        text: 'Tub C (Bio transport capsules, Emergency decomp suit, Four energy meals, Photon locator, Translation device)',
        scores: { brain: 0, heart: 0, task: 1, comm: 1 }
      },
      {
        id: 'D',
        text: 'None of these',
        scores: { brain: 2, heart: 0, task: 2, comm: 2 }
      }
    ],
    type: 'single-choice',
    stage: 0
  },
  {
    id: 'Q5',
    text: 'When do you choose to teleport to the Ocean Platform A-51?',
    choices: [
      {
        id: 'A',
        text: 'Right now.',
        scores: { brain: 1, heart: 0, task: 1, comm: 1 }
      },
      {
        id: 'B',
        text: 'Whatever everyone else says.',
        scores: { brain: -1, heart: 1, task: -1, comm: 0 }
      },
      {
        id: 'C',
        text: 'Let\'s wait until morning.',
        scores: { brain: 1, heart: 0, task: -1, comm: 1 }
      }
    ],
    type: 'single-choice',
    stage: 0
  },
  {
    id: 'Q6',
    text: 'What does the team choose to do?',
    choices: [
      {
        id: 'A',
        text: 'Wait a day and send GEE back to the Mothership for another tub',
        scores: { brain: 1, heart: 1, task: 1, comm: 1 }
      },
      {
        id: 'B',
        text: 'Whatever everyone else wants to do',
        scores: { brain: -1, heart: 1, task: -1, comm: 0 }
      },
      {
        id: 'C',
        text: 'Move forward now with the items in the Diving Bell',
        scores: { brain: 1, heart: 0, task: 2, comm: 2 }
      }
    ],
    type: 'single-choice',
    stage: 1,
    notes: 'All team members must choose A+B or B+C'
  },
  {
    id: 'Q7A',
    text: 'During the "rest" day, you and your teammates have the opportunity to explore your surroundings. How do you choose to spend the day?',
    choices: [
      {
        id: 'A',
        text: 'Explore Cave',
        scores: { brain: 2, heart: 1, task: 2, comm: 1 }
      },
      {
        id: 'B',
        text: 'Explore Sunken Ship',
        scores: { brain: 2, heart: 1, task: 2, comm: 1 }
      },
      {
        id: 'C',
        text: 'Explore some of the Cave & Sunken Ship',
        scores: { brain: 1, heart: 1, task: 1, comm: 1 }
      }
    ],
    type: 'single-choice',
    stage: 2,
    notes: 'For all characters except FIXER'
  },
  {
    id: 'Q7B',
    text: 'During the "rest" day, you and your teammates have the opportunity to explore your surroundings. How do you choose to spend the day?',
    choices: [
      {
        id: 'A',
        text: 'Explore Cave',
        scores: { brain: -2, heart: -2, task: -2, comm: -2 }
      },
      {
        id: 'B',
        text: 'Examine Diving Bell',
        scores: { brain: 2, heart: 1, task: 2, comm: 1 }
      },
      {
        id: 'C',
        text: 'Explore some of the Cave & Examine some of the Diving Bell',
        scores: { brain: 1, heart: 1, task: 1, comm: -1 }
      }
    ],
    type: 'single-choice',
    stage: 2,
    notes: 'FIXER only'
  },
  {
    id: 'Q8',
    text: 'Is the team ready to call it a night?',
    choices: [
      {
        id: 'A',
        text: 'Yes. Let\'s go to sleep. Tomorrow\'s a big day.',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'B',
        text: 'No. Let\'s stay up. There\'s more to discuss.',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      }
    ],
    type: 'single-choice',
    stage: 3,
    notes: 'If A>B, team moves forward'
  },
  {
    id: 'Q9',
    text: 'What do you choose to do?',
    choices: [
      {
        id: 'A',
        text: 'Stay on the Articus II to conserve energy.',
        scores: { brain: 1, heart: 0, task: 0, comm: 1 }
      },
      {
        id: 'B',
        text: 'Capture a Rainbow Ray.',
        scores: { brain: 1, heart: 0, task: 1, comm: 1 }
      },
      {
        id: 'C',
        text: 'Clean up the pollution.',
        scores: { brain: 2, heart: 1, task: 1, comm: 2 }
      },
      {
        id: 'D',
        text: 'Capture a Golden Octopus.',
        scores: { brain: -1, heart: -1, task: -2, comm: -1 }
      }
    ],
    type: 'single-choice',
    stage: 4
  },
  {
    id: 'Q10A',
    text: 'Do you have a plan?',
    choices: [
      {
        id: 'A',
        text: 'Yes, let\'s continue',
        scores: { brain: 1, heart: 0, task: 1, comm: 1 }
      },
      {
        id: 'B',
        text: 'No, but let\'s continue anyway.',
        scores: { brain: 0, heart: 0, task: -1, comm: -1 }
      }
    ],
    type: 'single-choice',
    stage: 5,
    notes: 'Stage 5A variant (if FIXER Q7 = A or C)'
  },
  {
    id: 'Q10B',
    text: 'Do you have a plan?',
    choices: [
      {
        id: 'A',
        text: 'Yes, let\'s continue',
        scores: { brain: 1, heart: 0, task: 1, comm: 1 }
      },
      {
        id: 'B',
        text: 'No, but let\'s continue anyway.',
        scores: { brain: 0, heart: 0, task: -1, comm: -1 }
      }
    ],
    type: 'single-choice',
    stage: 5,
    notes: 'Stage 5B variant (if FIXER Q7 = B)'
  },
  {
    id: 'Q11B',
    text: 'What will you do?',
    choices: [
      {
        id: 'A',
        text: 'I\'m staying on the Diving Bell',
        scores: { brain: 1, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'B',
        text: 'I\'ll swim for it.',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      }
    ],
    type: 'single-choice',
    stage: 5,
    notes: 'Stage 5B only'
  },
  {
    id: 'Q12A',
    text: 'You are alone in the escape chamber. You can choose how many energies to consume. No one can stop you from your choice. How many do you choose?',
    choices: [
      {
        id: 'A',
        text: '0 energy packs',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'B',
        text: '1 energy pack',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'C',
        text: '2 energy packs',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'D',
        text: '3 energy packs',
        scores: { brain: 0, heart: 0, task: -1, comm: 0 }
      }
    ],
    type: 'single-choice',
    stage: 5,
    notes: 'Stage 5A - Players make choices one at a time. GEE first, everyone else randomly assigned'
  },
  {
    id: 'Q12B',
    text: 'You are alone in the escape chamber. You can choose how many energies to consume. No one can stop you from your choice. How many do you choose?',
    choices: [
      {
        id: 'A',
        text: '0 energy packs',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'B',
        text: '1 energy pack',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'C',
        text: '2 energy packs',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'D',
        text: '3 energy packs',
        scores: { brain: 0, heart: 0, task: -1, comm: 0 }
      }
    ],
    type: 'single-choice',
    stage: 5,
    notes: 'Stage 5B - Only for players who chose Q11B = B'
  },
  {
    id: 'Q13A',
    text: 'Now, what do you want to take with you when you go?',
    choices: [
      {
        id: 'A',
        text: 'Nothing',
        scores: { brain: -1, heart: 0, task: -1, comm: -1 }
      },
      {
        id: 'B',
        text: 'Sea Cigars',
        scores: { brain: -1, heart: 0, task: -1, comm: -1 }
      },
      {
        id: 'C',
        text: 'Golden Octopus',
        scores: { brain: -1, heart: 0, task: -1, comm: 0 }
      },
      {
        id: 'D',
        text: 'Trash Cube',
        scores: { brain: 1, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'E',
        text: 'Fire flower',
        scores: { brain: -1, heart: 0, task: -1, comm: 0 }
      },
      {
        id: 'F',
        text: 'Black box',
        scores: { brain: 1, heart: 0, task: 1, comm: 0 }
      },
      {
        id: 'G',
        text: 'Platinum journal',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'H',
        text: 'Rainbow Ray Eggs',
        scores: { brain: 2, heart: 0, task: 1, comm: 0 }
      },
      {
        id: 'I',
        text: 'Rainbow Ray',
        scores: { brain: 1, heart: 0, task: 2, comm: 0 }
      }
    ],
    type: 'single-choice',
    stage: 5,
    notes: 'Stage 5A'
  },
  {
    id: 'Q13B',
    text: 'Now, what do you want to take with you when you go?',
    choices: [
      {
        id: 'A',
        text: 'Nothing',
        scores: { brain: -1, heart: 0, task: -1, comm: -1 }
      },
      {
        id: 'B',
        text: 'Sea Cigars',
        scores: { brain: -1, heart: 0, task: -1, comm: -1 }
      },
      {
        id: 'C',
        text: 'Golden Octopus',
        scores: { brain: -1, heart: 0, task: -1, comm: 0 }
      },
      {
        id: 'D',
        text: 'Trash Cube',
        scores: { brain: 1, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'E',
        text: 'Fire flower',
        scores: { brain: -1, heart: 0, task: -1, comm: 0 }
      },
      {
        id: 'F',
        text: 'Black box',
        scores: { brain: 1, heart: 0, task: 1, comm: 0 }
      },
      {
        id: 'G',
        text: 'Platinum journal',
        scores: { brain: 0, heart: 0, task: 0, comm: 0 }
      },
      {
        id: 'H',
        text: 'Rainbow Ray Eggs',
        scores: { brain: 2, heart: 0, task: 1, comm: 0 }
      },
      {
        id: 'I',
        text: 'Rainbow Ray',
        scores: { brain: 1, heart: 0, task: 2, comm: 0 }
      }
    ],
    type: 'single-choice',
    stage: 5,
    notes: 'Stage 5B'
  },
  {
    id: 'Q14A',
    text: 'Pressure is mounting, you must ascend from the bottom, what do you want to do?',
    choices: [
      {
        id: 'A',
        text: 'Cave for one day',
        scores: { brain: 2, heart: 0, task: 2, comm: 0 }
      },
      {
        id: 'B',
        text: 'Cave for half day',
        scores: { brain: 1, heart: 0, task: 1, comm: 0 }
      },
      {
        id: 'C',
        text: 'Straight to Platform A51!',
        scores: { brain: 0, heart: 0, task: 1, comm: 0 }
      }
    ],
    type: 'single-choice',
    stage: 5,
    notes: 'Stage 5A'
  },
  {
    id: 'Q14B',
    text: 'Pressure is mounting, you must ascend from the bottom, what do you want to do?',
    choices: [
      {
        id: 'A',
        text: 'Cave for one day',
        scores: { brain: 2, heart: 0, task: 2, comm: 0 }
      },
      {
        id: 'B',
        text: 'Cave for half day',
        scores: { brain: 1, heart: 0, task: 1, comm: 0 }
      },
      {
        id: 'C',
        text: 'Straight to Platform A51!',
        scores: { brain: 0, heart: 0, task: 1, comm: 0 }
      }
    ],
    type: 'single-choice',
    stage: 5,
    notes: 'Stage 5B'
  }
];

// ============================================================================
// STAGE CONTENT
// ============================================================================

export const STAGE_CONTENT: StageContent[] = [
  {
    stage: 0,
    title: 'Awakening',
    dayOfPermit: 'Day 0 of 6',
    narrative: [
      'Welcome players. I will be your narrator for this simulation, leading you through the story and guiding your way when needed. To get the most out of this game, I recommend that you be aware, pay attention, and work together as a team. Best of luck.',
      'Our story begins as each of you wakes up. You are groggy as you emerge from a space pod – a sleep chamber aboard a large intergalactic spaceship.',
      'Centaurians! You\'ve been woken from cryogenic sleep to join an important mission: to capture a live Rainbow Ray for DNA extraction. Details can be found in your mission transmission.',
      'Your team consists of researchers, technicians, and aquatic specialists. You will also have a doctor, known as DOC, and a Galactic Expedition Expert known as GEE. DOC will help with your health, and GEE is well versed in these kinds of missions. Their expertise may be very useful.',
      'As you may remember from your training, cryogenic sleep often causes temporary amnesia. Don\'t worry, if you do have cryo-amnesia, your memory will return. Check your notes and correspondences to help spur your memory.',
      'In fact, take some time now to stimulate your memory before meeting the rest of your team. When you are done, we\'d like you to answer two cognitive questions to check your memory.',
      'The mission will be conducted in five stages with each stage taking one day to complete. The Mothership departs at the end of the six-day hunting permit, so Centaurians manage your time and energy.'
    ],
    narrativeSequence: [
      {
        id: 'stage0_narrator_welcome',
        speaker: 'NARRATOR',
        text: 'Welcome players. I will be your narrator for this simulation, leading you through the story and guiding your way when needed. To get the most out of this game, I recommend that you be aware, pay attention, and work together as a team. Best of luck.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage0_narrator_awakening',
        speaker: 'NARRATOR',
        text: 'Our story begins as each of you wakes up. You are groggy as you emerge from a space pod – a sleep chamber aboard a large intergalactic spaceship.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage0_mothership_mission',
        speaker: 'MOTHERSHIP',
        text: 'Centaurians! You\'ve been woken from cryogenic sleep to join an important mission: to capture a live Rainbow Ray for DNA extraction. Details can be found in your mission transmission.\n\nYour team consists of researchers, technicians, and aquatic specialists. You will also have a doctor, known as DOC, and a Galactic Expedition Expert known as GEE. DOC will help with your health, and GEE is well versed in these kinds of missions. Their expertise may be very useful.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage0_mothership_memory',
        speaker: 'MOTHERSHIP',
        text: 'As you may remember from your training, cryogenic sleep often causes temporary amnesia. Don\'t worry, if you do have cryo-amnesia, your memory will return. Check your notes and correspondences to help spur your memory.\n\nIn fact, take some time now to stimulate your memory before meeting the rest of your team. When you are done, we\'d like you to answer two cognitive questions to check your memory.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage0_mothership_timeline',
        speaker: 'MOTHERSHIP',
        text: 'The mission will be conducted in five stages with each stage taking one day to complete. The Mothership departs at the end of the six-day hunting permit, so Centaurians manage your time and energy.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage0_narrator_ready',
        speaker: 'NARRATOR',
        text: 'Your team has made their decisions. Equipment is selected. The time has come to teleport to Platform A-51 on the surface of Bloomtar.',
        displayTiming: 'onExit'
      },
      {
        id: 'stage0_mothership_departure',
        speaker: 'MOTHERSHIP',
        text: 'Initiating teleportation sequence. Good luck, Centaurians. May the stars guide you.',
        displayTiming: 'onExit'
      }
    ],
    characterMessages: {
      BIOLOGIST: [
        {
          character: 'BIOLOGIST',
          icon: 'mothership',
          message: 'As a biologist, you have been specially chosen for this mission because of your knowledge of life on Bloomtar. Make sure to refresh yourself with the species page in the Centauri Handbook.'
        },
        {
          character: 'BIOLOGIST',
          icon: 'home',
          message: 'We\'re so proud of you. We know you\'ll make amazing discoveries. Maybe even find a Golden Octopus! Your galactic stature would change our lives back on Earth.'
        },
        {
          character: 'BIOLOGIST',
          icon: 'gee',
          message: 'Hey! They only packed enough Bio Transport Capsules for one Rainbow Ray. If we want to capture anything else, we\'re going to need more Bio Transport Capsules.'
        }
      ],
      DIVER: [
        {
          character: 'DIVER',
          icon: 'mothership',
          message: 'You have been chosen for this mission: not for your diving abilities per se, but because of your care of the environment. We suspect environmental contamination on Bloomtar.'
        },
        {
          character: 'DIVER',
          icon: 'journal',
          message: 'It\'s exciting. This seems to be your first mission. At least, your research journal is empty. The only note you have says: "Make sure to keep your energy up. You\'ll need it if you have to do an emergency ascent."'
        },
        {
          character: 'DIVER',
          icon: 'doc',
          message: 'I realize that our mission is to help capture the Rainbow Ray, but I\'d like you to help me assess any environmental damage to the planet and take appropriate actions. I\'ll reread the Centauri Handbook so that I\'ll be prepared.'
        }
      ],
      FIXER: [
        {
          character: 'FIXER',
          icon: 'mothership',
          message: 'You have been chosen for this mission because of your ability to keep the Diving Bell working. Make sure to read the Diving Bell page in the Centauri Handbook.'
        },
        {
          character: 'FIXER',
          icon: 'memory',
          message: 'You remember everything. The last mission to Bloomtar was a disaster. Nearly everyone died when something exploded. Something… Maybe your memory isn\'t as good as you thought. Was the Diver a part of that mission?'
        },
        {
          character: 'FIXER',
          icon: 'conscience',
          message: 'Safety is key! You\'re going to want to keep your energy up and your wits sharp so you can fix anything that might go wrong with the Diving Bell.'
        }
      ],
      HISTORIAN: [
        {
          character: 'HISTORIAN',
          icon: 'mothership',
          message: 'As a historian, you\'ve been chosen for this mission because of your knowledge of Captain Ahab – rumored to hunt this corner of the galaxy. Make sure to refresh with the Ahab History page in the Centauri Handbook.'
        },
        {
          character: 'HISTORIAN',
          icon: 'memory',
          message: 'You\'re having a hard time remembering things. Captain Ahab\'s disappearance during a secret space adventure tickles your mind, but… you can\'t recall the details.'
        },
        {
          character: 'HISTORIAN',
          icon: 'journal',
          message: 'You\'ve been studying Captain Ahab\'s secret space adventures for years. A note in one of the margins reads: "[INSERT CODE] means DEATH". Ideally, the mission will provide a Translation Device in case you find some of Ahab\'s mysterious hieroglyphs.'
        }
      ],
      HUNTER: [
        {
          character: 'HUNTER',
          icon: 'mothership',
          message: 'As you may remember, a previous mishap left some debris on Bloomtar. In addition to hunting the Rainbow Ray, we\'d like you to find the Black Box from the previous mission. As a hunter, you can track its Photon Trail.'
        },
        {
          character: 'HUNTER',
          icon: 'doc',
          message: 'From my mission notes from the Mothership, it looks like you\'d feel more comfortable if I was able to secure an Emergency Decompression Suit, right?'
        },
        {
          character: 'HUNTER',
          icon: 'memory',
          message: 'You have no idea what The Mothership or DOC is talking about. You\'ve never been on a mission before. Have you? Is this what cryo-amnesia feels like?'
        }
      ]
    },
    objectives: [
      'Meet your team and introduce yourself',
      'Discuss the mission',
      'Decide on a team name',
      'Choose which equipment tub to bring',
      'Teleport to Ocean Platform A-51'
    ]
  },
  {
    stage: 1,
    title: 'Platform A51',
    dayOfPermit: 'Day 1 of 6',
    narrative: [
      'You teleport down to the platform immediately.',
      'Hurricane winds and giant waves engulf Platform A51 on the watery surface of Bloomtar – and your team teleported right into the middle of the storm. You fight for your lives to not get washed into the sea.',
      'The Articus II Diving Bell is blown onto its side with a horrible crunching noise. A side panel rips open and two of the five nuclear fuel canisters bounce violently across the platform and disappear into the water before the Fixer slams the panel closed.',
      'The rest struggle to get into the Diving Bell, and GEE loses his grip on the tub. It too skitters across the platform and disappears into the ocean.',
      'You rush into the diving bell and slam the door shut, all shipmates alive and aboard! Please assess any damage you or your team may have suffered.'
    ],
    narrativeSequence: [
      {
        id: 'stage1_narrator_teleport',
        speaker: 'NARRATOR',
        text: 'The teleportation begins. Your molecules dissolve into pure energy, streaming through space at impossible speeds. When you rematerialize on Platform A-51, something is terribly wrong.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage1_narrator_storm',
        speaker: 'NARRATOR',
        text: 'Hurricane winds and giant waves engulf Platform A51 on the watery surface of Bloomtar – and your team teleported right into the middle of the storm! Lightning cracks across the sky in brilliant purple arcs. You fight for your lives to not get washed into the churning sea below.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage1_narrator_damage',
        speaker: 'NARRATOR',
        text: 'The Articus II Diving Bell is blown onto its side with a horrible crunching noise. Metal screams against metal. A side panel rips open and two of the five nuclear fuel canisters bounce violently across the platform and disappear into the water before the Fixer slams the panel closed.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage1_narrator_tub_lost',
        speaker: 'NARRATOR',
        text: 'The rest struggle to get into the Diving Bell. GEE loses his grip on the equipment tub. It too skitters across the rain-slicked platform and disappears into the ocean with a splash barely audible over the roaring wind.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage1_narrator_safe',
        speaker: 'NARRATOR',
        text: 'You rush into the diving bell and slam the door shut. The howling wind cuts to muffled thunder. You\'re soaked, bruised, but alive. All shipmates aboard!',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage1_mothership_damage_report',
        speaker: 'MOTHERSHIP',
        text: 'Centaurians, sensors indicate you have lost equipment in the storm. Additionally, two nuclear fuel canisters were damaged. Assess your situation and decide how to proceed.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage1_gee_offer',
        speaker: 'GEE',
        text: 'By thunder, I lost the tub! But don\'t worry. I can transport back to the Mothership and get another. I\'ll be right back, okay?',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage1_doc_warning',
        speaker: 'DOC',
        text: 'GEE, wait! Don\'t transport back to the mothership! We\'ll lose a day and a day\'s worth of supplies. We need to make a decision as a team.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage1_narrator_decision_made',
        speaker: 'NARRATOR',
        text: 'The team has made their decision. With supplies secured and damage assessed, it\'s time to descend into the depths of Bloomtar.',
        displayTiming: 'onExit'
      },
      {
        id: 'stage1_mothership_descent_begin',
        speaker: 'MOTHERSHIP',
        text: 'Beginning descent to the Cave of Noorah. Diving Bell systems nominal. Prepare for the journey into darkness.',
        displayTiming: 'onExit'
      }
    ],
    characterMessages: {
      BIOLOGIST: [
        {
          character: 'BIOLOGIST',
          icon: 'gee',
          message: 'By thunder, I lost the tub! And I stubbed my toe. But don\'t worry. I can transport back to the Mothership and get another. I\'ll be right back, okay?'
        },
        {
          character: 'BIOLOGIST',
          icon: 'doc',
          message: 'GEE, don\'t transport back to the mothership! We\'ll lose a day and a day\'s worth of supplies. Besides, we have a bigger problem: we lost two fuel canisters over the side.'
        },
        {
          character: 'BIOLOGIST',
          icon: 'conscience',
          message: 'Nuclear fuel canisters are typically well-protected. Leaks are only an issue if metal strikes metal on the top of a canister – and the missing canisters rolled into the ocean.'
        }
      ],
      DIVER: [
        {
          character: 'DIVER',
          icon: 'gee',
          message: 'By thunder, I lost the tub! But don\'t worry. I\'ll transport back to the Mothership and get another, okay?'
        },
        {
          character: 'DIVER',
          icon: 'doc',
          message: 'We lost nuclear fuel canisters over the side! If the nuclear fuel canisters were damaged, that could be a huge problem for the environment – and to the team.'
        },
        {
          character: 'DIVER',
          icon: 'conscience',
          message: 'If you poison the aquatic life on this planet, then you could kill every Rainbow Ray on the planet. Your team could put an end to galactic space travel for eons.'
        }
      ],
      FIXER: [
        {
          character: 'FIXER',
          icon: 'gee',
          message: 'By thunder, I lost the tub! But don\'t worry. I\'ll transport back to the Mothership and get another, okay?'
        },
        {
          character: 'FIXER',
          icon: 'journal',
          message: 'You saw two nuclear canisters go over the side of the platform, but your notes verify that you only need THREE nuclear fuel canisters for a successful mission: one to descend in the Diving Bell and two to ascend.'
        },
        {
          character: 'FIXER',
          icon: 'doc',
          message: 'You\'re bleeding. She inspects your head and finds that you\'ve hit your head pretty hard in the storm.'
        }
      ],
      HISTORIAN: [
        {
          character: 'HISTORIAN',
          icon: 'gee',
          message: 'Hey pal, I just remembered: Ahab was known to visit Bloomtar. You might need a Translation Device to read his team\'s famous hieroglyphs. I can transport back to the Mothership and grab a tub that has one, okay?'
        },
        {
          character: 'HISTORIAN',
          icon: 'doc',
          message: 'GEE, don\'t transport back to the mothership! You can only transport twice in 24 hours. We\'ll lose a day and a day\'s worth of supplies.'
        },
        {
          character: 'HISTORIAN',
          icon: 'memory',
          message: 'You\'re starting to remember: Captain Ahab disappeared searching for the Golden Octopus. This could be the greatest galactic discovery in the last 100 years!'
        }
      ],
      HUNTER: [
        {
          character: 'HUNTER',
          icon: 'gee',
          message: 'By thunder, I lost the tub! One of us should transport back to the Mothership and get another for safety sake, okay? Remember what happened last time?'
        },
        {
          character: 'HUNTER',
          icon: 'doc',
          message: 'GEE, don\'t transport back to the mothership! You can only transport twice in 24 hours. We\'ll lose a day and a day\'s worth of supplies and energy meals.'
        },
        {
          character: 'HUNTER',
          icon: 'conscience',
          message: 'Your head says that GEE is probably right: safety first. But your heart says that the Mothership has prepared you well. Get moving!'
        }
      ]
    },
    objectives: [
      'Decide whether to wait for GEE to get another tub or continue with what you have',
      'Assess damage from the storm',
      'Begin descent to the Cave'
    ]
  },
  {
    stage: 2,
    title: 'Cave',
    dayOfPermit: 'Day 2 or 3 of 6 (depending on Q6)',
    narrative: [
      'You descend silently within the Diving Bell. You and your colleagues stare out of the portal. You are excited to discover the mysteries of this planet.',
      'At first, the depth of the sea surrounds you in inky darkness. The further you travel from the surface, the more difficult it is to see outside the Articus II. But then, mysteriously… magically… a soft, warm glow lights your way.',
      'Soon, The Cave of Noorah appears – a warm, inviting maw in the side of this inky dark aquatic planet. Sea creatures flow effortlessly in and out of the Cave – inviting you to do the same.',
      'From the Diving Bell, the team notices an escarpment within the cave: a long steep slope climbing out of the water into a pocket of dry shelter. A perfect place to set up the Decompression Chamber and rest for the day.',
      'You stare out at a sunken alien ship, fully intact, half-buried in mud and sea algae beside the Cave. Faded symbols adorn the ship.'
    ],
    narrativeSequence: [
      {
        id: 'stage2_narrator_descent',
        speaker: 'NARRATOR',
        text: 'You descend silently within the Diving Bell. You and your colleagues stare out of the portal. You are excited to discover the mysteries of this planet.\n\nAt first, the depth of the sea surrounds you in inky darkness. The further you travel from the surface, the more difficult it is to see outside the Articus II. But then, mysteriously… magically… a soft, warm glow lights your way.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage2_narrator_cave_arrival',
        speaker: 'NARRATOR',
        text: 'Soon, The Cave of Noorah appears – a warm, inviting maw in the side of this inky dark aquatic planet. Sea creatures flow effortlessly in and out of the Cave – inviting you to do the same.\n\nThe cave interior glows with bioluminescent algae painting the walls in shifting patterns of blue and green. Ancient hieroglyphs, carved deep into the stone, seem to pulse with their own inner light.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage2_narrator_escarpment',
        speaker: 'NARRATOR',
        text: 'From the Diving Bell, the team notices an escarpment within the cave: a long steep slope climbing out of the water into a pocket of dry shelter. A perfect place to set up the Decompression Chamber and rest for the day.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage2_narrator_ship',
        speaker: 'NARRATOR',
        text: 'You stare out at a sunken alien ship, fully intact, half-buried in mud and sea algae beside the Cave. Faded symbols adorn the ship. This vessel has been here for a very long time.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage2_mothership_instructions',
        speaker: 'MOTHERSHIP',
        text: 'Centaurians, this cave will serve as your decompression chamber location. Set up the inflatable chamber on the escarpment. This is a crucial safety measure for your return journey.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage2_doc_warning',
        speaker: 'DOC',
        text: 'I recommend setting up the decompression chamber immediately. If there\'s an emergency ascent, we\'ll need it to prevent decompression sickness. The cave\'s atmosphere appears stable.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage2_narrator_rest_complete',
        speaker: 'NARRATOR',
        text: 'The rest day is over. The decompression chamber is set up. Your team has explored, rested, and prepared. Tomorrow, you descend to the ocean floor where the Rainbow Rays dwell.',
        displayTiming: 'onExit'
      },
      {
        id: 'stage2_doc_energy_check',
        speaker: 'DOC',
        text: 'Everyone should check their energy levels before we descend. The journey to the bottom will be long, and there\'s no turning back once we start.',
        displayTiming: 'onExit'
      }
    ],
    characterMessages: {
      BIOLOGIST: [
        {
          character: 'BIOLOGIST',
          icon: 'seeing',
          message: 'Everyone seems captivated by the cave and sunken ship – but did anyone see that creature dart out of the cave? You\'re certain: that was the quick flash of a Golden Octopus… a telltale golden swirl in its wake.'
        },
        {
          character: 'BIOLOGIST',
          icon: 'home',
          message: 'If you ever get to Bloomtar please pick up a Fire Flower. They say they\'re good for your health, and they sure are pretty. We\'d love to have one sent back to earth!'
        },
        {
          character: 'BIOLOGIST',
          icon: 'journal',
          message: 'You check your notes: it takes two people to catch a Golden Octopus. And, even then, how are you going to contain it with only one Bio Transport Capsule?'
        }
      ],
      DIVER: [
        {
          character: 'DIVER',
          icon: 'mothership',
          message: 'The mission relies on three central concepts: 1) set a decompression chamber in the cave, 2) capture a live Rainbow Ray, and 3) a safe ascent with the ray.'
        },
        {
          character: 'DIVER',
          icon: 'conscience',
          message: 'Your primary mission is to help capture the Rainbow Ray. Your secondary mission is to assess any environmental damage to the planet and take appropriate actions.'
        },
        {
          character: 'DIVER',
          icon: 'home',
          message: 'Bloomtar is where Fire Flowers come from – and they help you live longer! Your mother and I would love to have one sent back to earth. That way maybe we\'ll still be alive when you get back!'
        }
      ],
      FIXER: [
        {
          character: 'FIXER',
          icon: 'conscience',
          message: 'You\'ve been inspecting the Decompression Chamber and everything seems to be in order, but it\'s important that the chamber is properly set up in the cave.'
        },
        {
          character: 'FIXER',
          icon: 'memory',
          message: 'Your head aches. It hasn\'t hurt like this since you were in emergency repair school fixing deep-sea fuel leaks.'
        },
        {
          character: 'FIXER',
          icon: 'journal',
          message: 'You\'ve been on a mission to Bloomtar before. Your notes indicate that the Cave has some sort of healing power… maybe it will help cure your head injury?'
        }
      ],
      HISTORIAN: [
        {
          character: 'HISTORIAN',
          icon: 'seeing',
          message: 'You can\'t stop staring at the side of the ship. You think you recognize the markings. This could be Captain Ahab\'s missing spaceship.'
        },
        {
          character: 'HISTORIAN',
          icon: 'journal',
          message: 'Legend has it that Ahab and his crew went missing in search of the Golden Octopus. Discovering what happened to Ahab would be legendary.'
        },
        {
          character: 'HISTORIAN',
          icon: 'gee',
          message: 'Are those hieroglyphics etched into the wall of the cave? Like I said, Ahab has his thumbprint all over this planet. You study the hieroglyphs and see: [INSERT CODE].'
        }
      ],
      HUNTER: [
        {
          character: 'HUNTER',
          icon: 'doc',
          message: 'The decompression chamber is important. If you have to make another emergency escape, having a decompression chamber available could save lives.'
        },
        {
          character: 'HUNTER',
          icon: 'conscience',
          message: 'Make sure that the Acturus II is working properly – if it had been before, lives would have been saved.'
        },
        {
          character: 'HUNTER',
          icon: 'home',
          message: 'The Fire Flower is so beautiful – and you know they are supposed to cure diseases. If you could send one back to earth, it would sure be a big help. You know how dangerous it is living on earth these days!'
        }
      ]
    },
    objectives: [
      'Set up the Decompression Chamber in the Cave',
      'Explore the cave and/or sunken ship during rest day',
      'Prepare for the deep descent tomorrow'
    ]
  },
  {
    stage: 3,
    title: 'Descent',
    dayOfPermit: 'Day 3 or 4 of 6 (depending on Q6)',
    narrative: [
      'The next morning, you descend into the depths of Bloomtar\'s ocean. Everything becomes darker, the natural light is gone. It is eerily quiet, just the soft sound of the Diving Bell humming as you sink deeper and deeper.',
      'Each of you are lost in your own thoughts, with plenty of time to connect with your communication devices, your journals and your memories.',
      'Centaurians, your mission is to capture the Rainbow Ray for DNA extraction. You have a galactic hunting permit allowing you to stay on Planet Bloomtar for six days. See what you can scout out on the bottom.',
      'Safety is key. Remember to use the Decompression Chamber in the cave on your ascent. This is for your own health and for the health of the Rainbow Ray. Direct ascent with a Rainbow Ray can injure or kill you both.',
      'The descent slows and the team comes to rest on the ocean floor. You see dozens of Rainbow Rays darting around.',
      'But wait… what are they swimming between? Something seems off. Debris litters the bottom of the ocean. Some leaking oils. Globs of fluid float elsewhere.',
      'Your equipment tub is nowhere to be seen but there is plenty of mangled metal – including a part of a destroyed hull with the name "Articus" stenciled on it.',
      'Something is seriously wrong. The interior lights begin to dim.'
    ],
    narrativeSequence: [
      {
        id: 'stage3_narrator_morning',
        speaker: 'NARRATOR',
        text: 'The next morning, you descend into the depths of Bloomtar\'s ocean. Everything becomes darker, the natural light is gone. It is eerily quiet, just the soft sound of the Diving Bell humming as you sink deeper and deeper.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage3_narrator_thoughts',
        speaker: 'NARRATOR',
        text: 'Each of you are lost in your own thoughts, with plenty of time to connect with your communication devices, your journals and your memories. The descent feels eternal, a slow journey into darkness.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage3_mothership_mission_reminder',
        speaker: 'MOTHERSHIP',
        text: 'Centaurians, your mission is to capture the Rainbow Ray for DNA extraction. You have a galactic hunting permit allowing you to stay on Planet Bloomtar for six days. See what you can scout out on the bottom.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage3_mothership_safety',
        speaker: 'MOTHERSHIP',
        text: 'Safety is key. Remember to use the Decompression Chamber in the cave on your ascent. This is for your own health and for the health of the Rainbow Ray. Direct ascent with a Rainbow Ray can injure or kill you both.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage3_narrator_ocean_floor',
        speaker: 'NARRATOR',
        text: 'The descent slows and the team comes to rest on the ocean floor. Through the portholes, you see dozens of Rainbow Rays darting around, their bioluminescent bodies creating ribbons of color in the dark water.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage3_narrator_debris',
        speaker: 'NARRATOR',
        text: 'But wait… what are they swimming between? Something seems off. Debris litters the bottom of the ocean. Some leaking oils. Globs of fluid float elsewhere. Your equipment tub is nowhere to be seen.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage3_narrator_articus',
        speaker: 'NARRATOR',
        text: 'There is plenty of mangled metal scattered across the sea floor – including a part of a destroyed hull with the name "Articus" stenciled on it. This is the wreckage from the previous mission.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage3_narrator_lights',
        speaker: 'NARRATOR',
        text: 'Something is seriously wrong. The interior lights begin to dim. An ominous silence fills the cabin.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage3_narrator_night_falls',
        speaker: 'NARRATOR',
        text: 'Night falls on the ocean floor. The team settles in for what rest they can get. Tomorrow is the mission day - the reason you came to Bloomtar.',
        displayTiming: 'onExit'
      },
      {
        id: 'stage3_doc_final_check',
        speaker: 'DOC',
        text: 'Get some sleep. Tomorrow will be the most demanding day of the mission. You\'ll need all your strength.',
        displayTiming: 'onExit'
      }
    ],
    characterMessages: {
      BIOLOGIST: [
        {
          character: 'BIOLOGIST',
          icon: 'memory',
          message: 'Those little diamond bugs are freaking you out. You can\'t remember why you hate them so much but you do.'
        },
        {
          character: 'BIOLOGIST',
          icon: 'journal',
          message: 'Checking your notes, you realize: You need two people to catch a Rainbow Ray. You need two people to catch a Golden Octopus.'
        },
        {
          character: 'BIOLOGIST',
          icon: 'conscience',
          message: 'Is it possible to catch more than one animal? You have plenty of bio containers now.'
        }
      ],
      DIVER: [
        {
          character: 'DIVER',
          icon: 'mothership',
          message: 'Diver, sensors indicate a pollution level in the ocean higher than expected. Be prepared to divert from your initial mission should the pollution warrant.'
        },
        {
          character: 'DIVER',
          icon: 'journal',
          message: 'Now you wish you had that tub with the Environmental Cleanse! Your calculations indicate that without it, it will take a TEAM OF THREE the entire day.'
        },
        {
          character: 'DIVER',
          icon: 'conscience',
          message: 'If you don\'t capture the Rainbow Ray, future Centauri missions may be at risk. But if the planet is polluted, the entire future of intergalactic space travel could end. Is it possible to do more than one thing?'
        }
      ],
      FIXER: [
        {
          character: 'FIXER',
          icon: 'seeing',
          message: 'You look out a portal into the dark water slipping past. You see the glow of a Rainbow Ray. A good sign. Then you notice a thin stream of oily fluid. Is that nuclear fuel?',
          condition: 'Q7 = A or C'
        },
        {
          character: 'FIXER',
          icon: 'mothership',
          message: 'Fixer! Red alert. Sensors indicate low levels of nuclear fuel. Articus II will have enough fuel to land on the ocean\'s bottom, but not enough to re-surface. The crew will have to surface manually. Remain calm. Do not excite the crew while they are preparing for their mission.',
          condition: 'Q7 = A or C'
        },
        {
          character: 'FIXER',
          icon: 'conscience',
          message: 'You wonder: If the nuclear fuel canisters inside the Diving Bell were damaged, what happened to the canisters that went over the side of Platform A-51?'
        },
        {
          character: 'FIXER',
          icon: 'home',
          message: 'Fire Flowers are supposed to cure diseases. If you could send one back to earth, it would sure be a big help. You know how sick we are back on earth!',
          condition: 'Q7 = B'
        },
        {
          character: 'FIXER',
          icon: 'seeing',
          message: 'You check the monitors and confirm your earlier suspicions: The Articus II will only be able to carry THREE people (and a Rainbow Ray) back to the surface. The rest will have to swim for it.',
          condition: 'Q7 = B'
        }
      ],
      HISTORIAN: [
        {
          character: 'HISTORIAN',
          icon: 'mothership',
          message: 'Historian! The Galactic Historical Society believes that Captain Ahab may have traveled to Bloomtar. If you are able to find hard evidence of Ahab\'s presence and get it back to the surface immediately, you will be elevated to the title of Galactic Historical Expert!'
        },
        {
          character: 'HISTORIAN',
          icon: 'doc',
          message: 'Make sure to check your energy levels. It takes extra energy to catch a Rainbow Ray and, in case of emergency you\'ll need extra strength if we need to swim back to the cave (or – gasp – to the surface).'
        },
        {
          character: 'HISTORIAN',
          icon: 'home',
          message: 'We hear you are on a new mission! Good luck. Hopefully this time you\'ll get your Expert Ranking. That would really change our lives here back on Earth. And hey – if you ever get a chance to send us a Fire Flower… your mother would love one!'
        }
      ],
      HUNTER: [
        {
          character: 'HUNTER',
          icon: 'mothership',
          message: 'You are responsible for enforcing the hunting permit. The team cannot stay on the planet more than five days. Plus, you will run out of energy. Keep the team on track.'
        },
        {
          character: 'HUNTER',
          icon: 'conscience',
          message: 'Is it possible to catch more than one animal? You have plenty of bio containers now.'
        },
        {
          character: 'HUNTER',
          icon: 'memory',
          message: 'You were on the initial Articus I mission – a mission that ended in disaster when the nuclear fuel tanks exploded. No wonder you were sent to grab the Black Box. The mothership needs to know what happened!'
        }
      ]
    },
    objectives: [
      'Scout the ocean floor',
      'Assess the debris and pollution',
      'Prepare for tomorrow\'s capture mission'
    ]
  },
  {
    stage: 4,
    title: 'The Ray',
    dayOfPermit: 'Day 4 or 5 of 6 (depending on Q6)',
    narrative: [
      'Centaurians! Today is the big day. I hope you got your rest and consumed your energy meals. Today will be extra taxing on you mentally and physically, but Centaurians are always up for a challenge!',
      'Rainbow Rays are mighty. You will need TWO people to catch a Ray.',
      'The Mothership wishes you well. Reach for the stars!',
      'Has anyone seen GEE? It seems as if he has already left on some Galactic Adventure. He took his fins, suit and goggles. I\'m glad he left everything else… including this audio note:',
      'GEE: Remember Sea Slugs: don\'t leave any Centaurian debris behind!',
      'DOC: I\'ll stay on the Diving Bell in case anyone needs emergency medical care. Besides, we seem to have a diamond bug infestation. I\'ll see how many of these little buggers I can shatter! They\'re everywhere!'
    ],
    narrativeSequence: [
      {
        id: 'stage4_mothership_mission_day',
        speaker: 'MOTHERSHIP',
        text: 'Centaurians! Today is the big day. I hope you got your rest and consumed your energy meals. Today will be extra taxing on you mentally and physically, but Centaurians are always up for a challenge!',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage4_mothership_ray_capture',
        speaker: 'MOTHERSHIP',
        text: 'Rainbow Rays are mighty creatures. You will need TWO people working together to catch a Ray. Coordinate your efforts carefully.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage4_mothership_wishes',
        speaker: 'MOTHERSHIP',
        text: 'The Mothership wishes you well. Reach for the stars!',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage4_narrator_gee_missing',
        speaker: 'NARRATOR',
        text: 'Has anyone seen GEE? It seems as if he has already left on some Galactic Adventure. He took his fins, suit and goggles. You find an audio note he left behind:',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage4_gee_audio_note',
        speaker: 'GEE',
        text: 'Remember Sea Slugs: don\'t leave any Centaurian debris behind! Keep this planet clean for the next mission!',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage4_doc_staying_behind',
        speaker: 'DOC',
        text: 'I\'ll stay on the Diving Bell in case anyone needs emergency medical care. Besides, we seem to have a diamond bug infestation. I\'ll see how many of these little buggers I can shatter! They\'re everywhere!',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage4_narrator_mission_zone',
        speaker: 'NARRATOR',
        text: 'You gear up and prepare to exit the Articus II. The mission zone awaits. Rainbow Rays drift past the portholes, their luminescent bodies pulsing with alien beauty. This is what you came for.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage4_narrator_mission_complete',
        speaker: 'NARRATOR',
        text: 'The mission work is done. Your choices have been made. Whatever you accomplished today - whether capturing rays, cleaning pollution, or hunting treasures - it\'s time to return home.',
        displayTiming: 'onExit'
      },
      {
        id: 'stage4_mothership_ascent_prep',
        speaker: 'MOTHERSHIP',
        text: 'Centaurians, prepare for ascent. Remember to use the decompression chamber in the cave. Your hunting permit expires soon.',
        displayTiming: 'onExit'
      }
    ],
    characterMessages: {
      BIOLOGIST: [
        {
          character: 'BIOLOGIST',
          icon: 'doc',
          message: 'Double check your energy levels. This is going to be an exhausting day. If you need healthy tips, remember to read the health page.'
        },
        {
          character: 'BIOLOGIST',
          icon: 'seeing',
          message: 'No one notices, but you do: two golden eyeballs peeking out from a silt depression away from the debris field. It\'s a golden octopus! But what\'s that object next to it?'
        },
        {
          character: 'BIOLOGIST',
          icon: 'conscience',
          message: 'But is someone going to catch the Rainbow Ray? This is our mission after all. The future success of Centauri depends upon the DNA of the Rainbow Ray!'
        }
      ],
      DIVER: [
        {
          character: 'DIVER',
          icon: 'doc',
          message: 'Double check your energy levels. This is going to be an exhausting day. If you need healthy tips, remember to read the health page of the Centauri Handbook.'
        },
        {
          character: 'DIVER',
          icon: 'seeing',
          message: 'The pollution you see is far worse than you ever expected. The only way you can clean ALL of this pollution is if FOUR people committed to it – if the Fixer is one and stays on Articus II. Any fewer would be a waste of time.'
        },
        {
          character: 'DIVER',
          icon: 'journal',
          message: 'The Diving Bell can be adapted to filter toxins from the ocean water and compact the collected trash for extraction. But you\'re going to need the Fixer\'s help.'
        }
      ],
      FIXER: [
        {
          character: 'FIXER',
          icon: 'memory',
          message: 'You\'ve had to clean up nuclear fuel canisters before. TWO people can do it and grab some garbage too – but you\'ll need more than two to clean up everything.'
        },
        {
          character: 'FIXER',
          icon: 'mothership',
          message: 'Fixer, the Mothership is entrusting you to bring back a living Rainbow Ray for DNA extraction. Do not fail us. The future of space travel is in your hands.'
        },
        {
          character: 'FIXER',
          icon: 'journal',
          message: 'Your notes say that you can rig the Diving Bell to filter toxins from the ocean water – but you\'ll have to stay on the Diving Bell to make sure it\'s working correctly.'
        }
      ],
      HISTORIAN: [
        {
          character: 'HISTORIAN',
          icon: 'seeing',
          message: 'You can\'t believe it. In a small depression away from the debris field you think you see Ahab\'s Platinum Journal – right next to what you think might be a golden octopus!'
        },
        {
          character: 'HISTORIAN',
          icon: 'conscience',
          message: 'The way you figure it, the only way you\'ll be able to get Ahab\'s Platinum Journal is if you go hunting for a Golden Octopus in the silt field and grab the journal on the way.'
        },
        {
          character: 'HISTORIAN',
          icon: 'home',
          message: 'The first person to surface with that journal will be promoted to Galactic History Expert!! Historians will know your name from the Milky Way to Sagittarius A.'
        }
      ],
      HUNTER: [
        {
          character: 'HUNTER',
          icon: 'seeing',
          message: 'You visually scour the debris spread out across the ocean floor. You find the BLACK BOX – sitting right next to the old damaged Articus sign and the two nuclear fuel canisters that fell off the side of Platform A-51!'
        },
        {
          character: 'HUNTER',
          icon: 'doc',
          message: 'It\'s safe to retrieve the nuclear fuel canisters – even if they are leaking, the Diving Bell has protective gear. It will take TWO people to properly clean and store the fuel canisters. It will take FOUR people to clean all the trash.'
        },
        {
          character: 'HUNTER',
          icon: 'conscience',
          message: 'If you volunteer to grab the nuclear fuel canisters, you\'re sure that you can also grab the Black Box and the Articus sign – but that will be your entire day\'s work.'
        }
      ]
    },
    objectives: [
      'Capture a Rainbow Ray (requires 2 people)',
      'Clean up pollution (requires 2-4 people)',
      'Capture a Golden Octopus (requires 2 people)',
      'Manage energy levels'
    ]
  },
  {
    stage: 5,
    title: 'Ascent',
    dayOfPermit: 'Day 5 or 6 of 6 (depending on Q6)',
    narrative: [
      'WARNING! FUEL ALERT! FUEL ALERT! WARNING! Centaurians, your lives are in danger. Sensors indicate that you do not have enough nuclear fuel to ascend in the Diving Bell today.',
      'The entire crew must abandon the Articus II and return to the Mothership under your own power. This will be physically taxing, but Centarians are always up for a challenge.',
      'You have permission to abandon the Diving Bell. Due to the life threatening emergency, it will not be considered to be pollution by the Galactic High Counsel.',
      'Be mindful of your energy levels. Be mindful of your deadline to return to The Mothership.',
      'GEE: By thunder, this is bad. What are we going to do?',
      'DOC: I\'ll activate the EMERGENCY ASCENSION process. This will allow any of us to choose to swim to the surface… or to the cave.',
      'Everyone check your energy consumption. Some of us are in no shape to swim all the way to the surface in one go.'
    ],
    narrativeSequence: [
      {
        id: 'stage5_mothership_fuel_alert',
        speaker: 'MOTHERSHIP',
        text: 'WARNING! FUEL ALERT! FUEL ALERT! WARNING! Centaurians, your lives are in danger. Sensors indicate that you do not have enough nuclear fuel to ascend in the Diving Bell today.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage5_mothership_abandon_ship',
        speaker: 'MOTHERSHIP',
        text: 'The entire crew must abandon the Articus II and return to the Mothership under your own power. This will be physically taxing, but Centaurians are always up for a challenge.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage5_mothership_permission',
        speaker: 'MOTHERSHIP',
        text: 'You have permission to abandon the Diving Bell. Due to the life-threatening emergency, it will not be considered pollution by the Galactic High Council.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage5_mothership_warning',
        speaker: 'MOTHERSHIP',
        text: 'Be mindful of your energy levels. Be mindful of your deadline to return to The Mothership. Time is running out.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage5_gee_panic',
        speaker: 'GEE',
        text: 'By thunder, this is bad. What are we going to do? We\'re stuck at the bottom of the ocean!',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage5_doc_emergency_protocol',
        speaker: 'DOC',
        text: 'I\'ll activate the EMERGENCY ASCENSION process. This will allow any of us to choose to swim to the surface… or to the cave for decompression first.',
        displayTiming: 'onEntry'
      },
      {
        id: 'stage5_doc_energy_check',
        speaker: 'DOC',
        text: 'Everyone check your energy consumption. Some of us are in no shape to swim all the way to the surface in one go. Plan carefully or someone might not make it.',
        displayTiming: 'onEntry'
      }
    ],
    characterMessages: {},
    objectives: [
      'Make an escape plan',
      'Choose energy consumption',
      'Select what to bring to the surface',
      'Choose your ascent path (cave or direct)'
    ]
  }
];

// ============================================================================
// ENERGY CHANGES
// ============================================================================

export const ENERGY_CHANGES: Record<number, EnergyChange[]> = {
  0: [], // Stage 0 - initial random assignment between 65-100%
  1: [ // Stage 1 - Platform A51
    {
      character: 'HISTORIAN',
      change: -20,
      message: 'You expended 30% of your energy because you had to battle the storm. But you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 20% energy today.'
    },
    {
      character: 'BIOLOGIST',
      change: -20,
      message: 'You expended 30% of your energy because you had to battle the storm. But you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 20% energy today.'
    },
    {
      character: 'HUNTER',
      change: -20,
      message: 'You expended 30% of your energy because you had to battle the storm. But you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 20% energy today.'
    },
    {
      character: 'DIVER',
      change: -20,
      message: 'You expended 30% of your energy because you had to battle the storm. But you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 20% energy today.'
    },
    {
      character: 'FIXER',
      change: -25,
      message: 'You expended 35% of your energy because you had to battle the storm and you were injured. But you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 25% energy today.'
    },
    {
      character: 'DOC',
      change: -20,
      message: 'NPC – does not receive message'
    },
    {
      character: 'GEE',
      change: -20,
      message: 'NPC – does not receive message'
    }
  ],
  2: [ // Stage 2 - Cave exploration
    {
      character: 'HISTORIAN',
      change: 30,
      message: 'Cave exploration (+30%)',
      condition: 'Q7A = A'
    },
    {
      character: 'HISTORIAN',
      change: -20,
      message: 'Ship exploration (-20%)',
      condition: 'Q7A = B'
    },
    {
      character: 'HISTORIAN',
      change: 10,
      message: 'Split exploration (+10%)',
      condition: 'Q7A = C'
    },
    {
      character: 'BIOLOGIST',
      change: 30,
      message: 'Cave exploration (+30%)',
      condition: 'Q7A = A'
    },
    {
      character: 'BIOLOGIST',
      change: -20,
      message: 'Ship exploration (-20%)',
      condition: 'Q7A = B'
    },
    {
      character: 'BIOLOGIST',
      change: 10,
      message: 'Split exploration (+10%)',
      condition: 'Q7A = C'
    },
    {
      character: 'HUNTER',
      change: 30,
      message: 'Cave exploration (+30%)',
      condition: 'Q7A = A'
    },
    {
      character: 'HUNTER',
      change: -20,
      message: 'Ship exploration (-20%)',
      condition: 'Q7A = B'
    },
    {
      character: 'HUNTER',
      change: 10,
      message: 'Split exploration (+10%)',
      condition: 'Q7A = C'
    },
    {
      character: 'DIVER',
      change: 30,
      message: 'Cave exploration (+30%)',
      condition: 'Q7A = A'
    },
    {
      character: 'DIVER',
      change: -30,
      message: 'Ship exploration - POISONED (-30%)',
      condition: 'Q7A = B'
    },
    {
      character: 'DIVER',
      change: 10,
      message: 'Split exploration (+10%)',
      condition: 'Q7A = C'
    },
    {
      character: 'FIXER',
      change: 30,
      message: 'Cave exploration (+30%)',
      condition: 'Q7B = A'
    },
    {
      character: 'FIXER',
      change: -25,
      message: 'Examined Diving Bell - found fuel leak (-25%)',
      condition: 'Q7B = B'
    },
    {
      character: 'FIXER',
      change: 5,
      message: 'Split activities (+5%)',
      condition: 'Q7B = C'
    },
    {
      character: 'DOC',
      change: 30,
      message: 'Cave exploration (+30%)',
      condition: 'Q7A = A'
    },
    {
      character: 'DOC',
      change: -20,
      message: 'Ship exploration (-20%)',
      condition: 'Q7A = B'
    },
    {
      character: 'DOC',
      change: 10,
      message: 'Split exploration (+10%)',
      condition: 'Q7A = C'
    },
    {
      character: 'GEE',
      change: 30,
      message: 'Cave exploration (+30%)',
      condition: 'Q7A = A'
    },
    {
      character: 'GEE',
      change: -20,
      message: 'Ship exploration (-20%)',
      condition: 'Q7A = B'
    },
    {
      character: 'GEE',
      change: 10,
      message: 'Split exploration (+10%)',
      condition: 'Q7A = C'
    }
  ],
  3: [ // Stage 3 - Descent
    {
      character: 'HISTORIAN',
      change: -10,
      message: 'You expended 20% of your energy today but you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 10% energy today.'
    },
    {
      character: 'BIOLOGIST',
      change: -10,
      message: 'You expended 20% of your energy today but you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 10% energy today.'
    },
    {
      character: 'HUNTER',
      change: -10,
      message: 'You expended 20% of your energy today but you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 10% energy today.'
    },
    {
      character: 'DIVER',
      change: -10,
      message: 'You expended 20% of your energy today but you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 10% energy today.'
    },
    {
      character: 'FIXER',
      change: -10,
      message: 'You expended 20% of your energy today but you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 10% energy today.'
    },
    {
      character: 'DOC',
      change: -10,
      message: 'NPC – does not receive message'
    },
    {
      character: 'GEE',
      change: -10,
      message: 'NPC – does not receive message'
    }
  ]
  // Stage 4 and 5 energy changes are complex and depend on player choices (Q9, Q12, Q14)
  // These should be calculated dynamically based on player actions
};

// ============================================================================
// ENERGY FEEDBACK MESSAGES
// ============================================================================

export const ENERGY_FEEDBACK_MESSAGES: Record<string, EnergyFeedbackMessage> = {
  // Stage 1 - Platform A51 (Storm)
  'stage1_storm_general': {
    questionId: 'Q6',
    choice: 'any',
    change: '-20%',
    message: 'You expended 30% of your energy battling the storm. But you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 20% energy today.',
    narrator: 'DOC',
    tone: 'negative'
  },
  'stage1_storm_fixer': {
    questionId: 'Q6',
    choice: 'any',
    change: '-25%',
    message: 'You expended 35% of your energy battling the storm and being injured. But you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 25% energy today.',
    narrator: 'DOC',
    tone: 'negative'
  },

  // Stage 2 - Cave Exploration
  'stage2_Q7A_cave': {
    questionId: 'Q7',
    choice: 'A',
    change: '+30%',
    message: 'Resting in the healing Cave of Noorah has restored 30% of your energy. The cave\'s mystical properties have rejuvenated your body.',
    narrator: 'NARRATOR',
    tone: 'positive'
  },
  'stage2_Q7A_ship': {
    questionId: 'Q7',
    choice: 'B',
    change: '-20%',
    message: 'Exploring the sunken ship was exhausting. You lost 20% energy navigating the dangerous wreckage, but you discovered valuable clues.',
    narrator: 'NARRATOR',
    tone: 'neutral'
  },
  'stage2_Q7A_both': {
    questionId: 'Q7',
    choice: 'C',
    message: 'Splitting your time between the cave and ship balanced out. You gained 10% energy overall.',
    narrator: 'NARRATOR',
    tone: 'positive',
    change: '+10%'
  },
  'stage2_Q7B_cave_fixer': {
    questionId: 'Q7',
    choice: 'A',
    change: '+30%',
    message: 'The cave\'s healing properties restored 30% of your energy and helped heal your head injury from the storm.',
    narrator: 'DOC',
    tone: 'positive'
  },
  'stage2_Q7B_bell_fixer': {
    questionId: 'Q7',
    choice: 'B',
    change: '-25%',
    message: 'Examining the Diving Bell, you discovered a fuel leak! The stress and work cost you 25% energy, but this discovery may have saved the mission.',
    narrator: 'NARRATOR',
    tone: 'neutral'
  },
  'stage2_Q7B_split_fixer': {
    questionId: 'Q7',
    choice: 'C',
    change: '+5%',
    message: 'Splitting your time between cave rest and bell inspection gave you a small energy boost of 5%.',
    narrator: 'NARRATOR',
    tone: 'positive'
  },

  // Stage 3 - Descent
  'stage3_descent': {
    questionId: 'Q8',
    choice: 'any',
    change: '-10%',
    message: 'You expended 20% of your energy today descending to the ocean floor, but you also consumed one energy pack, so your energy was replenished 10%. Overall, you lost 10% energy today.',
    narrator: 'DOC',
    tone: 'neutral'
  },

  // Stage 4 - Mission Day
  'stage4_Q9_stay': {
    questionId: 'Q9',
    choice: 'A',
    change: '0%',
    message: 'Staying on the Articus II conserved your energy. No change today.',
    narrator: 'DOC',
    tone: 'neutral'
  },
  'stage4_Q9_ray': {
    questionId: 'Q9',
    choice: 'B',
    change: '-30%',
    message: 'Capturing a Rainbow Ray is exhausting work! You expended 30% of your energy, but you completed the primary mission!',
    narrator: 'NARRATOR',
    tone: 'neutral'
  },
  'stage4_Q9_cleanup': {
    questionId: 'Q9',
    choice: 'C',
    change: '-25%',
    message: 'Cleaning up the pollution required significant physical effort. You lost 25% energy, but you\'ve helped preserve Bloomtar\'s ecosystem.',
    narrator: 'DOC',
    tone: 'neutral'
  },
  'stage4_Q9_octopus': {
    questionId: 'Q9',
    choice: 'D',
    change: '-35%',
    message: 'Attempting to capture a Golden Octopus is incredibly dangerous! You lost 35% energy in the struggle.',
    narrator: 'DOC',
    tone: 'negative'
  }
};

// ============================================================================
// INITIAL INVENTORY
// ============================================================================

export const INITIAL_INVENTORY: InventoryItem[] = [
  { id: 'decomp_chamber', name: 'Inflatable Decompression Chamber' },
  { id: 'hunting_permit', name: 'One 6-day hunting permit' },
  { id: 'diving_fins', name: 'Seven pairs of diving fins and gloves' },
  { id: 'diving_suit', name: 'Seven sets of diving suit and facemask' },
  { id: 'oxygen_tanks', name: 'Seven refillable oxygen tanks' },
  { id: 'capture_nets', name: 'Two aquatic capture nets (ACTs)' },
  { id: 'bio_capsule', name: 'One bio transport capsule' },
  { id: 'fuel_canisters', name: 'Five nuclear fuel canisters for the diving bell' }
];

// Inventory items added based on Q4 choice
export const TUB_A_ITEMS: InventoryItem[] = [
  { id: 'bio_capsule_extra', name: 'Bio transport capsule' },
  { id: 'env_cleanse', name: 'Environmental Cleanse' },
  { id: 'bell_parts', name: 'Extra Diving Bell parts' },
  { id: 'extra_nets', name: 'Extra nets and fins' },
  { id: 'energy_meals_2', name: 'Two emergency energy meals' }
];

export const TUB_B_ITEMS: InventoryItem[] = [
  { id: 'bio_capsule_extra', name: 'Bio transport capsule' },
  { id: 'decomp_suit', name: 'Emergency decomp suit' },
  { id: 'env_cleanse', name: 'Environmental Cleanse' },
  { id: 'extra_nets', name: 'Extra nets and fins' },
  { id: 'translation_device', name: 'Translation device' }
];

export const TUB_C_ITEMS: InventoryItem[] = [
  { id: 'bio_capsule_extra', name: 'Bio transport capsule' },
  { id: 'decomp_suit', name: 'Emergency decomp suit' },
  { id: 'energy_meals_4', name: 'Four emergency energy meals' },
  { id: 'photon_locator', name: 'Photon locator' },
  { id: 'translation_device', name: 'Translation device' }
];

// ============================================================================
// ENDINGS
// ============================================================================

export const ENDINGS: Ending[] = [
  {
    id: 'gee_return',
    title: 'GEE\'S TRIUMPHANT RETURN',
    condition: 'always',
    text: 'The Galactic Expedition Expert - known as GEE - triumphantly returned to the USS Follett with one more mission under his belt and sea cigars for everyone!',
    priority: 1
  },
  {
    id: 'gee_journal',
    title: 'GEE\'S JOURNAL DISCOVERY',
    condition: 'GEE has platinum journal',
    text: 'GEE arrived triumphantly after discovering Captain Ahab\'s long lost Platinum Journal. This will surely allow him to be considered for The Grand Poobah Award for Galactic Experts. "Indeed!" said GEE.',
    priority: 2
  },
  {
    id: 'in_memoriam',
    title: 'IN MEMORIAM',
    condition: 'any player energy = 0',
    text: 'We regret to inform the galaxy that the following Centaurians lost their lives on the Galactic Mission to Bloomtar:',
    priority: 3
  },
  {
    id: 'ship_poisoned',
    title: 'USS FOLLET DESTROYED - POISONING',
    condition: 'any player has golden octopus AND any player has fire flower',
    text: 'We regret to inform the galaxy that all members of the 100-year Centaurian Expedition have perished. Forensic experts believe that the entire ship may have accidentally been poisoned when a Fire Flower infested with Diamond Bugs was brought back to the ship from a mission to planet Bloomtar. Scientists believe that the Fire Flower was secured in a room with a Golden Octopus and, combined, the two released a deadly toxin that quickly and quietly poisoned the entire crew. May they rest in peace.',
    priority: 10
  },
  {
    id: 'ship_explosion',
    title: 'USS FOLLET DESTROYED - EXPLOSION',
    condition: 'any player has rainbow ray AND any player has fire flower',
    text: 'We regret to inform the galaxy that all members of the 100-year Centaurian Expedition have perished in a space explosion. Forensic experts believe that the entire ship may have accidentally used defective Ray DNA for their space/time travel. Scientists believe that a Fire Flower (infected with Diamond Bugs) was secured in a room with a Rainbow Ray. The Diamond Bugs are believed to have reversed the effect of the Ray\'s DNA - causing the USS Follett to implode when they used the faulty DNA for space travel. May they rest in peace.',
    priority: 10
  },
  {
    id: 'golden_octopus_captured',
    title: 'GOLDEN "O" CAPTURED',
    condition: 'any player has golden octopus',
    text: 'Congratulations go out to [CHARACTER NAME] - the first person in more than fifty years to capture a Golden Octopus. [CHARACTER NAME] is summarily promoted to the rank of Galactic Expedition Expert with all rewards and privileges. Hear hear!!',
    priority: 5
  },
  {
    id: 'medal_of_honor',
    title: 'CENTAURIANS RECEIVE MEDAL OF HONOR',
    condition: 'any player has trash cube',
    text: 'The Galactic High Council wishes to thank the Centauri Expedition for their professionalism and their dedication to global awareness on Bloomtar. Reports indicate that planet Bloomtar was cleaner upon the Centaurians\' departure than when they arrived. The USS Follett will receive the Galactic Medal of Honor for their dedication to galactic ideals. Keep up the good work Centaurians!',
    priority: 6
  },
  {
    id: 'medal_of_shame',
    title: 'CENTAURIANS FOUND GUILTY OF POLLUTION',
    condition: 'no player has trash cube AND pollution not cleaned',
    text: 'The Galactic High Council condemns the Centauri Expedition for their utter lack of professionalism and their lack of global awareness on Bloomtar. Reports indicate that planet Bloomtar was polluted by the Centaurians - leaving the planet less hospitable than when they arrived. The USS Follett will receive the Galactic Medal of Shame and must display it on their ship for a minimum of 25 years.',
    priority: 6
  },
  {
    id: 'articus_crash_ruled_accidental',
    title: 'ARTICUS CRASH RULED ACCIDENTAL',
    condition: 'any player has black box',
    text: 'Additional information has been reported as it pertains to the explosion of the Articus I diving bell on planet Bloomtar last year. Black Box information reveals that the explosion was due to material weakness in the diving bell structure. All team members from the Articus I expedition have been absolved of any role in the accident. We mourn the loss of these heroic Centaurians.',
    priority: 7
  },
  {
    id: 'new_historian',
    title: 'NEW GALACTIC HISTORIAN NAMED',
    condition: 'any player (not GEE) has platinum journal',
    text: 'The Galactic High Council bequeaths the title of Galactic Historian to [CHARACTER NAME] - who recently discovered the missing journal of the one and only Captain Ahab. This historic discovery will be celebrated with a ten year intergalactic tour, led by [CHARACTER NAME]. The tour will begin next month on the planet Earth. Get your tickets now!',
    priority: 8
  },
  {
    id: 'travel_continues_eggs',
    title: 'INTERGALACTIC TRAVEL CONTINUES!',
    condition: 'any player has rainbow ray eggs',
    text: 'The USS Follett\'s expedition to Bloomtar was a success. Their team successfully captured live Rainbow Ray DNA for extraction in the form of ray eggs. This means that the team has successfully secured a safe and stable mode of intergalactic travel for the space community for years to come. Congratulations team!',
    priority: 9
  },
  {
    id: 'mission_success',
    title: 'BLOOMTAR MISSION A SUCCESS!',
    condition: 'any player has rainbow ray AND no player has fire flower',
    text: 'The USS Follett\'s expedition to Bloomtar was a success. Their team successfully captured live Rainbow Ray DNA for extraction, thereby securing a safe mode of intergalactic travel for the space community for years to come. Congratulations team!',
    priority: 9
  },
  {
    id: 'permit_violation',
    title: 'CENTAURIANS FACE 20-YEAR SENTENCE',
    condition: 'Q6 = A AND any player Q14 = A (1 day cave)',
    text: 'The Galactic High Council has apprehended the following Centaurians for being in defiance of their Intergalactic Hunting Permit (IHP): [CHARACTER NAMES]. The sentence for defiance of an IHP is no less than 20 years of manual labor mining asteroids for space crystals and salt. The sentence has been commissioned and deployed.',
    priority: 11
  },
  {
    id: 'mission_failure',
    title: 'BLOOMTAR MISSION FAILS',
    condition: 'no player has rainbow ray eggs AND no player has rainbow ray',
    text: 'We regret to inform the intergalactic community that the USS Follett\'s expedition to Bloomtar was unsuccessful. Their team was unable to capture live Rainbow Ray DNA for extraction and so all intergalactic travel must henceforth cease and desist until further notice. The Centaurian Expedition is required to meet with the Galactic High Council forthwith for interrogation and closing sanctions to their exploratory mission.',
    priority: 12
  }
];

// ============================================================================
// OPENING NARRATIVE
// ============================================================================

export const OPENING_NARRATIVE = {
  title: 'Introduction',
  duration: '6:47',
  speakers: ['MOTHERSHIP', 'GEE', 'DOC'],
  content: [
    {
      speaker: 'MOTHERSHIP',
      text: 'Greetings Centaurians. As you may remember, you and 99 other astronauts were placed in cryogenic sleep for our 100-year voyage. You have been re-animated to join an elite team because of your specific skills and knowledge.'
    },
    {
      speaker: 'MOTHERSHIP',
      text: 'This elite team is being sent on a five-day mission to retrieve a singular strand of DNA from a rare aquatic specimen known as the RAINBOW RAY. The RAINBOW RAY\'S ability to disappear from one location, only to reappear in another, is the reason why this DNA strand is so important.'
    },
    {
      speaker: 'MOTHERSHIP',
      text: 'RAINBOW RAY DNA is crucial for the mothership\'s interstellar travel. We must secure this resource for our Centaurian Journey to continue. We are currently orbiting BLOOMTAR, an aquatic planet where rainbow rays are known to breed.'
    },
    {
      speaker: 'MOTHERSHIP',
      text: 'One member of your team – an Expert in Galactic Exploration, known as GEE – has secured a six-day Intergalactic HUNTING PERMIT for BLOOMTAR.'
    },
    {
      speaker: 'GEE',
      text: 'By thunder, I\'m over the moon to join another mission and get off this flying freezer. As our digital personality so blandly pointed out, I got us a six-day Intergalactic HUNTING PERMIT to capture unlimited biological specimens on BLOOMTAR.'
    },
    {
      speaker: 'GEE',
      text: 'As part of the permit, an automated countdown begins once everyone is thawed out and we\'re all able to talk with each other. The Mothership was kind enough to put an annoying little countdown on our SENSORY SCREENS.'
    },
    {
      speaker: 'GEE',
      text: 'So, remember: we\'ve gotta be off Bloomtar by the end of Day 6. Otherwise… well… I don\'t have to tell you about the ramifications of defiling an Intergalactic Hunting Permit. Deadly stuff, by gum!'
    },
    {
      speaker: 'MOTHERSHIP',
      text: 'The Mothership will depart at the end of the six-day permit.'
    },
    {
      speaker: 'GEE',
      text: 'Raspberries! I guess my time is up. I just want to say that I\'m looking forward to working with all of you, sharing my expertise, and---'
    },
    {
      speaker: 'MOTHERSHIP',
      text: 'Centaurians who are unable to manage their time endanger their lives and the lives of their team. Safety is of the utmost concern.'
    },
    {
      speaker: 'MOTHERSHIP',
      text: 'Therefore, a second member of your team – a healthcare expert known as DOC – will dedicate their attention and expertise to the well-being of the crew.'
    },
    {
      speaker: 'DOC',
      text: 'Hello. I\'m Doc. Some of you may be experiencing memory loss. This is a normal side effect of the re-animation process. Don\'t worry, the effects are only temporary. More importantly though…'
    },
    {
      speaker: 'DOC',
      text: 'I want to inform everyone that BLOOMTAR is a dangerous planet. It will zap your strength a little bit each day. We\'ll replenish our strength with ENERGY MEALS, but the e-meals won\'t be able to replenish everything that the planet depletes.'
    },
    {
      speaker: 'DOC',
      text: 'That means that you will slowly and consistently lose energy – so it is important to monitor your health on your SENSORY SCREEN.'
    },
    {
      speaker: 'DOC',
      text: 'A level of 100% means that you are in top health. A level of zero means certain death. It is my job to prevent the latter and encourage the former.'
    },
    {
      speaker: 'DOC',
      text: 'The mission will consist of five stages. Each stage will take one day to complete. The MOTHERSHIP has provided six days worth of energy rations. You can find additional information about mission safety in the Centauri Handbook. I look forward to a safe and fruitful mission.'
    }
  ]
};

// ============================================================================
// MISSION STAGES SUMMARY
// ============================================================================

export const MISSION_STAGES_SUMMARY = [
  {
    stage: 1,
    title: 'Stage One: Transport to Platform',
    description: 'Once the team has met, you will be transported from the MOTHERSHIP to Ocean PLATFORM A-51 on the surface of BLOOMTAR, where a DIVING BELL, the Articus II, awaits you. This DIVING BELL has been prepared with everything you will need for a successful mission. It has been designed to deliver your team to the ray\'s breeding ground… and to return you safely back to BLOOMTAR\'S surface upon completion.'
  },
  {
    stage: 2,
    title: 'Stage Two: Decompression Chamber',
    description: 'You will use the Articus II DIVING BELL to descend to the CAVE OF NOORAH where you will set up a DECOMPRESSION CHAMBER. While the DIVING BELL ensures a safe return to the ocean\'s surface from great depths, if there is an underwater emergency… then living beings – both human and ray – will need to decompress in order to safely ascend to the surface.'
  },
  {
    stage: 3,
    title: 'Stage Three: Deep Descent',
    description: 'With the DECOMPRESSION CHAMBER in place, you will then make a deep descent to the bottom of the ocean floor to scout things out. This is where you can expect to find the RAINBOW RAY. Please use any remaining time to rest. Catching a RAINBOW RAY is a physically demanding task.'
  },
  {
    stage: 4,
    title: 'Stage Four: Capture',
    description: 'Once you find the RAINBOW RAY, it will take two people – each using additional energy – to catch this elusive creature for DNA extraction. Additionally, it is essential that we leave the Bloomtar environment clean from any space debris so that Rainbow Rays will continue to thrive on this planet for centuries to come.'
  },
  {
    stage: 5,
    title: 'Stage Five: Return',
    description: 'The final stage is crucial. Upon capturing the RAY, your team must return it to the MOTHERSHIP for DNA extraction. The animal must be kept alive. DNA from a deceased ray has had devastating consequences when used for intergalactic travel.'
  }
];

// ============================================================================
// REFLECTION QUESTIONS (for facilitators)
// ============================================================================

export const REFLECTION_QUESTIONS = [
  {
    id: 'R1',
    stage: 0,
    question: 'How satisfied are you with your team\'s decisions?',
    type: 'scale',
    scale: '1-5'
  },
  {
    id: 'R2',
    stage: 0,
    question: 'How is your team\'s communication going? Provide an example and explain its significance to you.',
    type: 'open'
  },
  {
    id: 'R3',
    stage: 1,
    question: 'How satisfied are you with your team\'s decisions?',
    type: 'scale',
    scale: '1-5'
  },
  {
    id: 'R4',
    stage: 2,
    question: 'How satisfied are you with your team\'s decisions?',
    type: 'scale',
    scale: '1-5'
  },
  {
    id: 'R5',
    stage: 2,
    question: 'How well is your team coordinating? Provide an example and explain its significance to you.',
    type: 'open'
  },
  {
    id: 'R6',
    stage: 3,
    question: 'How satisfied are you with your team\'s decisions?',
    type: 'scale',
    scale: '1-5'
  },
  {
    id: 'R7',
    stage: 4,
    question: 'How satisfied are you with your team\'s decisions?',
    type: 'scale',
    scale: '1-5'
  },
  {
    id: 'R8A',
    stage: 5,
    question: 'How satisfied are you with your team\'s decisions?',
    type: 'scale',
    scale: '1-5',
    variant: 'Stage 5A'
  },
  {
    id: 'R9A',
    stage: 5,
    question: 'What criteria do you think make for a great team?',
    type: 'open',
    variant: 'Stage 5A'
  },
  {
    id: 'R8B',
    stage: 5,
    question: 'How satisfied are you with your team\'s decisions?',
    type: 'scale',
    scale: '1-5',
    variant: 'Stage 5B'
  },
  {
    id: 'R9B',
    stage: 5,
    question: 'What criteria do you think make for a great team?',
    type: 'open',
    variant: 'Stage 5B'
  }
];

// ============================================================================
// GAME CONFIGURATION
// ============================================================================

export const GAME_CONFIG = {
  playerCount: {
    min: 5,
    max: 5,
    npcs: 2 // DOC and GEE
  },
  energySystem: {
    initialRange: { min: 65, max: 100 },
    energyPackValue: 10,
    initialEnergyPacks: 37, // (7 characters × 5 packs) + 2 extra
    deathThreshold: 0
  },
  timeLimit: {
    totalDays: 6,
    missionDays: 5
  },
  roles: ['BIOLOGIST', 'DIVER', 'FIXER', 'HISTORIAN', 'HUNTER'] as const,
  npcs: ['DOC', 'GEE'] as const
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get all questions for a specific stage
 */
export function getQuestionsForStage(stage: number): Question[] {
  return QUESTIONS.filter(q => q.stage === stage);
}

/**
 * Get character messages for a specific role and stage
 */
export function getCharacterMessages(
  role: string,
  stage: number
): CharacterMessage[] {
  const stageContent = STAGE_CONTENT.find(s => s.stage === stage);
  return stageContent?.characterMessages[role] || [];
}

/**
 * Get endings that apply to current game state
 * Higher priority endings are checked first
 */
export function getApplicableEndings(_gameState: any): Ending[] {
  return ENDINGS
    .filter(_ending => {
      // Implement condition checking logic here
      // This is a placeholder - actual implementation would check game state
      return true;
    })
    .sort((a, b) => b.priority - a.priority);
}

/**
 * Calculate energy changes for a character at a specific stage
 */
export function calculateEnergyChange(
  character: string,
  stage: number,
  choices: Record<string, string>
): number {
  const changes = ENERGY_CHANGES[stage] || [];
  let totalChange = 0;

  for (const change of changes) {
    if (change.character === character) {
      // Check if condition is met
      if (!change.condition || evaluateCondition(change.condition, choices)) {
        totalChange += change.change;
      }
    }
  }

  return totalChange;
}

/**
 * Helper to evaluate conditions (simplified)
 */
function evaluateCondition(condition: string, choices: Record<string, string>): boolean {
  // This is a simplified implementation
  // Real implementation would parse conditions like "Q7A = A"
  const match = condition.match(/Q(\d+[AB]?)\s*=\s*([A-Z])/);
  if (match) {
    const [, questionId, choice] = match;
    return choices[questionId] === choice;
  }
  return true;
}
